import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mlm/ui';
import { MemberPicker } from '../components/member-picker';
import { useHasPerm } from '../lib/auth-store';
import { type AdminUserSearchResult } from '../lib/api';
import {
  grantCourseEnrollment,
  listAllCourses,
  listCourseEnrollments,
  revokeCourseEnrollment,
  type AdminCourseEnrollmentRow,
  type AdminCourseRow,
} from '../lib/creators-api';

const sourceLabel: Record<string, string> = {
  purchase: 'Purchase',
  free: 'Free',
  subscriber: 'Subscriber',
  admin_grant: 'Admin grant',
};

type EnrollmentState = 'active' | 'revoked' | 'expired';

/**
 * Revoked beats expired: a row can be both, and the deliberate act is the one
 * an admin needs to see.
 */
function enrollmentState(row: AdminCourseEnrollmentRow): EnrollmentState {
  if (row.revokedAt) return 'revoked';
  if (row.expiresAt && new Date(row.expiresAt).getTime() <= Date.now()) return 'expired';
  return 'active';
}

const stateBadge: Record<
  EnrollmentState,
  { label: string; variant: 'success' | 'destructive' | 'warning' }
> = {
  active: { label: 'Active', variant: 'success' },
  revoked: { label: 'Revoked', variant: 'destructive' },
  expired: { label: 'Expired', variant: 'warning' },
};

/**
 * Course enrollments — the recovery desk for a course sale that landed on the
 * wrong account. A POS sale always books to the member the cashier selected, so
 * a course rung on a shared walk-in login grants access to THAT account; this
 * page is the only way to move it.
 *
 * Deliberately small: pick a course, read its roster, grant or revoke. Phase 3
 * absorbs it into full course authoring.
 */
export function CourseEnrollmentsPage() {
  const canWrite = useHasPerm('product.write');
  const [courseId, setCourseId] = useState('');
  const [granting, setGranting] = useState(false);
  const [revoking, setRevoking] = useState<AdminCourseEnrollmentRow | null>(null);
  const qc = useQueryClient();

  // The course PICKER wants every course in one go (200 is the API's page cap),
  // which is a different query from the Creators page's paged Courses tab — so
  // it gets its own cache key.
  const coursesQuery = useQuery({
    queryKey: ['admin-all-courses-picker'],
    queryFn: () => listAllCourses({ pageSize: 200 }),
  });
  const courses = coursesQuery.data?.rows ?? [];
  const course = courses.find((c) => c.id === courseId);

  const rosterQuery = useQuery({
    queryKey: ['admin-course-enrollments', courseId],
    queryFn: () => listCourseEnrollments(courseId),
    enabled: !!courseId,
  });
  const roster = rosterQuery.data ?? [];

  // The roster drives the count column on the course list, so both refresh.
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin-course-enrollments', courseId] });
    qc.invalidateQueries({ queryKey: ['admin-all-courses'] });
  };

  const revoke = useMutation({
    mutationFn: (enrollmentId: string) => revokeCourseEnrollment(courseId, enrollmentId),
    meta: { successMessage: 'Access revoked.' },
    onSuccess: () => {
      invalidate();
      setRevoking(null);
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Course enrollments</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Who holds access to a course, and the tool to fix it when a sale landed on the wrong
          account. A POS sale books to the member the cashier selected — a course rung on a shared
          walk-in login grants access to that login, not the buyer.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Course</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select value={courseId} onValueChange={setCourseId}>
            <SelectTrigger className="max-w-xl">
              <SelectValue placeholder={coursesQuery.isLoading ? 'Loading…' : 'Pick a course…'} />
            </SelectTrigger>
            <SelectContent>
              {courses.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {courseOptionLabel(c)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!coursesQuery.isLoading && courses.length === 0 && (
            <p className="text-sm text-muted-foreground">No courses yet.</p>
          )}
        </CardContent>
      </Card>

      {course && (
        <Card>
          <CardHeader className="flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle>
              Roster
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                {roster.length} {roster.length === 1 ? 'record' : 'records'}
              </span>
            </CardTitle>
            {canWrite && (
              <Button size="sm" onClick={() => setGranting(true)}>
                Grant access
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-3 p-0">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-muted-foreground">
                <tr>
                  <th className="p-3">Member</th>
                  <th className="p-3">Source</th>
                  <th className="p-3">Order</th>
                  <th className="p-3">Granted</th>
                  <th className="p-3">Progress</th>
                  <th className="p-3">Last activity</th>
                  <th className="p-3">Tests</th>
                  <th className="p-3">Certificate</th>
                  <th className="p-3">Status</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {roster.map((r) => {
                  const state = enrollmentState(r);
                  const badge = stateBadge[state];
                  return (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="p-3 font-medium">
                        {r.userName ?? <span className="text-muted-foreground">(no name)</span>}
                      </td>
                      <td className="p-3">{sourceLabel[r.source] ?? r.source}</td>
                      <td className="p-3 font-mono text-xs">
                        {r.orderCode ?? <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="p-3">{new Date(r.grantedAt).toLocaleDateString()}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-16 overflow-hidden rounded bg-muted">
                            <div
                              className="h-full bg-primary"
                              style={{ width: `${Math.min(100, r.progressPct)}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {r.lessonsCompleted}/{r.lessonCount}
                          </span>
                        </div>
                      </td>
                      <td className="p-3 text-muted-foreground">
                        {r.lastActivityAt ? new Date(r.lastActivityAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="p-3 text-muted-foreground">
                        {r.testsRequired > 0 ? `${r.testsPassed}/${r.testsRequired}` : '—'}
                      </td>
                      <td className="p-3 text-muted-foreground">
                        {r.certificateIssuedAt
                          ? new Date(r.certificateIssuedAt).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="p-3">
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                        {r.expiresAt && state !== 'revoked' && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            {state === 'expired' ? 'on ' : 'until '}
                            {new Date(r.expiresAt).toLocaleDateString()}
                          </div>
                        )}
                        {r.revokedAt && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            on {new Date(r.revokedAt).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        {canWrite && state === 'active' && (
                          <Button size="sm" variant="outline" onClick={() => setRevoking(r)}>
                            Revoke
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {rosterQuery.isLoading && (
                  <tr>
                    <td colSpan={10} className="p-8 text-center text-muted-foreground">
                      Loading…
                    </td>
                  </tr>
                )}
                {!rosterQuery.isLoading && roster.length === 0 && (
                  <tr>
                    <td colSpan={10} className="p-8 text-center text-muted-foreground">
                      Nobody is enrolled in this course yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <p className="px-3 pb-3 text-xs text-muted-foreground">
              Revoking is soft — the row stays, so progress history and any certificate already
              issued survive.{' '}
              {course.accessLevel === 'free'
                ? 'This course is FREE, so revoking will NOT lock the learner out: access to a free course never depended on the enrollment row, and the next lesson they open re-creates it.'
                : 'Revoking a FREE course does not lock the learner out — access there never depended on the enrollment row. It only bites on gated courses like this one.'}
            </p>
          </CardContent>
        </Card>
      )}

      {course && granting && (
        <GrantAccessDialog
          course={course}
          onClose={() => setGranting(false)}
          onGranted={() => {
            invalidate();
            setGranting(false);
          }}
        />
      )}

      <ConfirmDialog
        open={!!revoking}
        onOpenChange={(open) => {
          if (!open) setRevoking(null);
        }}
        title="Revoke course access?"
        description={
          revoking ? (
            <>
              <span className="font-medium">{revoking.userName ?? '(no name)'}</span> loses access
              to <span className="font-medium">{course?.productName}</span>. Their progress and any
              issued certificate are kept.
            </>
          ) : null
        }
        confirmLabel="Revoke"
        confirmVariant="destructive"
        loading={revoke.isPending}
        onConfirm={() => {
          if (revoking) revoke.mutate(revoking.id);
        }}
      />
    </div>
  );
}

function courseOptionLabel(c: AdminCourseRow): string {
  const parts = [c.creatorName ?? 'House', c.status];
  return `${c.productName} — ${parts.join(' · ')}`;
}

function GrantAccessDialog({
  course,
  onClose,
  onGranted,
}: {
  course: AdminCourseRow;
  onClose: () => void;
  onGranted: () => void;
}) {
  const [picked, setPicked] = useState<AdminUserSearchResult | null>(null);
  const [expiresOn, setExpiresOn] = useState('');

  const grant = useMutation({
    mutationFn: () =>
      grantCourseEnrollment(course.id, {
        userId: picked!.id,
        // "Expires on <date>" means access lasts THROUGH that day, so the
        // cut-off is the end of it — local midnight would expire the grant
        // before the member ever opened a lesson.
        expiresAt: expiresOn ? new Date(`${expiresOn}T23:59:59`).toISOString() : null,
      }),
    meta: { successMessage: 'Access granted.' },
    onSuccess: onGranted,
  });

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !grant.isPending) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Grant course access</DialogTitle>
          <DialogDescription>
            Give a member access to <span className="font-medium">{course.productName}</span>{' '}
            without a sale. If the member already has live access, revoke that enrollment first —
            this will not overwrite it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">Member</label>
            <MemberPicker selected={picked} onChange={setPicked} disabled={grant.isPending} />
          </div>
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">
              Expires on (blank = never expires)
            </label>
            <Input
              type="date"
              value={expiresOn}
              onChange={(e) => setExpiresOn(e.target.value)}
              disabled={grant.isPending}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={grant.isPending}>
            Cancel
          </Button>
          <Button onClick={() => grant.mutate()} disabled={!picked || grant.isPending}>
            {grant.isPending ? 'Granting…' : 'Grant access'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
